import { env } from "../../../config/env";
import { authStorage } from "../../../lib/storage/authStorage";
import type { FlyerTemplateItem, TemplateFieldDef } from "./templateTypes";

export type { TemplateFieldDef };
export type { FlyerTemplateItem };

export interface FlyerCategoryOption {
    slug: string;
    name: string;
    display_group: string;
    group_slug: string;
}

export const defaultTemplateFields = (): TemplateFieldDef[] => [];

export function serializeTemplateFields(fields: TemplateFieldDef[]): string {
    return JSON.stringify(
        fields.map(f => ({
            key: f.key.trim(),
            label: f.label.trim(),
            placeholder: f.placeholder.trim(),
            required: f.required,
            field_type: f.field_type || "text",
        })),
    );
}

function authHeaders(): HeadersInit {
    const token = authStorage.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function fetchFlyerCategoryOptions(): Promise<FlyerCategoryOption[]> {
    const res = await fetch(`${env.apiBaseUrl}/rag/flyer-template-categories`, {
        headers: { ...authHeaders() },
    });
    if (!res.ok) throw new Error("Failed to load categories");
    const data = (await res.json()) as { categories: FlyerCategoryOption[] };
    return data.categories ?? [];
}

/**
 * Init shape used by the flyer dev form.
 * Mirrors FlyerTemplateItem from the backend — all fields optional except id, name, category.
 */
export type FlyerTemplateInit = Partial<FlyerTemplateItem> & Pick<FlyerTemplateItem, "id" | "name" | "category">;

export interface CreateFlyerTemplateInput {
    category_slug: string;
    name: string;
    base_prompt?: string;
    fields: TemplateFieldDef[];
    preview_image: File;
    cta_text?: string;
    tagline?: string;
    style?: string;
    mood?: string;
    color_tone?: string;
    flyer_type?: string;
    overlay_style?: string;
    time_of_day?: string;
    aspect_ratio?: string;
    prompt_template?: string;
}

export type UpdateFlyerTemplateInput = Omit<CreateFlyerTemplateInput, "preview_image"> & {
    preview_image?: File;
};

function buildFlyerFormData(input: UpdateFlyerTemplateInput, includePreview?: File): FormData {
    const form = new FormData();
    form.append("category_slug", input.category_slug);
    form.append("name", input.name);
    form.append("base_prompt", input.base_prompt ?? "");
    form.append("fields_json", serializeTemplateFields(input.fields));
    if (includePreview) form.append("preview_image", includePreview);
    form.append("cta_text", input.cta_text ?? "BOOK NOW");
    form.append("tagline", input.tagline ?? "");
    form.append("style", input.style ?? "");
    form.append("mood", input.mood ?? "");
    form.append("color_tone", input.color_tone ?? "");
    form.append("flyer_type", input.flyer_type ?? "");
    form.append("overlay_style", input.overlay_style ?? "");
    form.append("time_of_day", input.time_of_day ?? "");
    form.append("aspect_ratio", input.aspect_ratio ?? "9:16");
    form.append("prompt_template", input.prompt_template ?? "");
    return form;
}

export async function createFlyerTemplateDev(input: CreateFlyerTemplateInput): Promise<void> {
    const form = buildFlyerFormData(input, input.preview_image);
    const res = await fetch(`${env.apiBaseUrl}/rag/flyer-templates`, {
        method: "POST",
        headers: authHeaders(),
        body: form,
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const detail = (err as { detail?: string }).detail ?? res.statusText;
        throw new Error(typeof detail === "string" ? detail : "Create failed");
    }
}

export async function updateFlyerTemplateDev(
    templateId: string,
    input: UpdateFlyerTemplateInput,
): Promise<void> {
    const form = buildFlyerFormData(input, input.preview_image);
    const res = await fetch(`${env.apiBaseUrl}/rag/flyer-templates/${encodeURIComponent(templateId)}`, {
        method: "PUT",
        headers: authHeaders(),
        body: form,
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const detail = (err as { detail?: string }).detail ?? res.statusText;
        throw new Error(typeof detail === "string" ? detail : "Update failed");
    }
}
